import {
  BookOpen,
  Globe2,
  Users2,
  GraduationCap,
  Clock,
  MapPin,
  CheckCircle2,
  Award,
  MessageSquare,
  BookOpenCheck,
  PenTool,
  CalendarDays,
} from "lucide-react";
import { useSiteContent } from "@/hooks/useSiteContent";

const COHORTS = [
  {
    name: "Cohort 1",
    dates: ["Tue 20 Oct 2026", "Tue 3 Nov 2026", "Tue 17 Nov 2026"],
  },
  {
    name: "Cohort 2",
    dates: ["Wed 21 Oct 2026", "Wed 4 Nov 2026", "Wed 18 Nov 2026"],
  },
];

export default function MicroCredential() {
  const c = useSiteContent();
  return (
    <section id="micro-credential" className="section bg-[var(--bone)]">
      <div className="container-pl">
        {/* Cohort Dates — prominent banner */}
        <div className="mb-14 md:mb-20" data-testid="cohort-dates-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[var(--forest)] text-white flex items-center justify-center">
              <CalendarDays size={20} />
            </div>
            <div>
              <div className="overline !text-[var(--forest)]">Save the dates</div>
              <h3 className="font-display font-bold text-2xl md:text-3xl leading-tight mt-1">
                Cohort dates · Term 4, 2026
              </h3>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {COHORTS.map((co) => (
              <div
                key={co.name}
                className="card !p-6 md:!p-8 bg-white border-[var(--border)]"
                data-testid={`cohort-dates-${co.name.toLowerCase().replace(" ", "-")}`}
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-display font-bold text-xl text-[var(--forest)]">
                    {co.name}
                  </h4>
                  <span className="text-[0.72rem] uppercase tracking-[0.2em] text-[var(--slate)] font-display font-semibold">
                    3 workshops
                  </span>
                </div>
                <ul className="mt-5 space-y-3">
                  {co.dates.map((d, i) => (
                    <li key={d} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-md bg-[var(--sage)] text-[var(--forest)] flex items-center justify-center text-xs font-display font-bold shrink-0">
                        {i + 1}
                      </div>
                      <span className="font-display font-semibold text-[var(--charcoal)]">
                        {d}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-5 border-t border-[var(--border)] text-xs text-[var(--slate)]">
                  Sir Edmund Hillary Collegiate · 12:30 PM – 4:30 PM
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5">
            <div className="overline" data-testid="mc-overline">The Micro-Credential</div>
            <h2 className="font-display font-bold text-3xl md:text-5xl mt-3 leading-tight">
              Practical Strategies to Support{" "}
              <span className="text-[var(--forest)]">
                English Language Learners
              </span>{" "}
              in Multilingual Classrooms
            </h2>
            <p className="mt-5 text-[var(--slate)] text-base md:text-lg leading-relaxed">
              The first structured, NZQA-accredited credential designed exclusively for
              teacher aides supporting ELL students in Aotearoa New Zealand classrooms.
            </p>

            <div id="programme" className="mt-10 card" data-testid="mc-programme-card">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--forest)] text-white text-xs font-display font-semibold">
                  <Award size={12} /> NZQA #5684
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--sage)] text-[var(--forest)] text-xs font-display font-semibold">
                  Level 4
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--sage)] text-[var(--forest)] text-xs font-display font-semibold">
                  5 Credits
                </span>
              </div>
              <h3 className="font-display font-semibold text-xl mt-4">{c.cohort_label}</h3>
              <ul className="mt-5 space-y-3">
                <Detail
                  icon={<Clock size={18} />}
                  label="Duration"
                  value={c.duration}
                />
                <Detail
                  icon={<MapPin size={18} />}
                  label="Delivery"
                  value={c.delivery}
                />
                <Detail
                  icon={<GraduationCap size={18} />}
                  label="Start"
                  value={c.start_label}
                />
                <Detail
                  icon={<CheckCircle2 size={18} />}
                  label="Who can enrol"
                  value={c.eligibility}
                />
              </ul>
              <a href="#register" className="btn btn-primary mt-7 w-full" data-testid="mc-register-cta">
                Enrol Now
              </a>
              <a
                href="#accreditation"
                className="mt-4 flex items-center justify-center gap-2 text-xs text-[var(--slate)] hover:text-[var(--forest)] transition"
                data-testid="mc-accreditation-link"
              >
                NZQA-accredited · Listing #5684 ↓
              </a>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="overline" data-testid="strands-overline">Three strands · Six workshops</div>
            <h3 className="font-display font-bold text-2xl md:text-3xl mt-3">
              Built around the three pillars of language learning
            </h3>
            <p className="mt-3 text-[var(--slate)] max-w-xl">
              Every strand is grounded in current research and aligned to Aotearoa&apos;s national
              frameworks — so what you learn translates directly into the classroom.
            </p>

            <div className="mt-8 grid sm:grid-cols-1 gap-5">
              <Strand
                icon={<MessageSquare size={22} />}
                label="Strand One"
                title="Oral Language"
                body="Build academic talk for ELL students through structured questioning, sentence frames, accountable talk and translanguaging — grounded in Cummins (BICS/CALP), Krashen (affective filter) and Gibbons."
                testid="strand-oral"
              />
              <Strand
                icon={<BookOpenCheck size={22} />}
                label="Strand Two"
                title="Reading"
                body="From the Simple View of Reading to Tier 1/2/3 vocabulary instruction, pre-reading scaffolds, comprehension strategies and culturally relevant text selection for ELL readers."
                testid="strand-reading"
              />
              <Strand
                icon={<PenTool size={22} />}
                label="Strand Three"
                title="Writing"
                body="Support ELLs to write with confidence — process writing, genre pedagogy, mentor texts, vocabulary in writing, and a feedback framework that affirms voice and identity."
                testid="strand-writing"
              />
            </div>

            <div className="mt-8 card !p-6 bg-[var(--sage)] border-transparent">
              <div className="overline">Grounded in</div>
              <p className="mt-3 text-[var(--slate)] text-sm leading-relaxed">
                <strong className="text-[var(--charcoal)]">Research:</strong> Cummins · Krashen ·
                Vygotsky · Gibbons · García &amp; Wei · Paris &amp; Alim · Beck, McKeown &amp; Kucan
                · Nation · Hattie &amp; Timperley
              </p>
              <p className="mt-3 text-[var(--slate)] text-sm leading-relaxed">
                <strong className="text-[var(--charcoal)]">Aligned to NZ frameworks:</strong>{" "}
                Te Mātaiaho · Tapasā · Ka Hikitia · ELLP · Tātaiako · The New Zealand Curriculum
              </p>
            </div>

            <div className="mt-6 grid sm:grid-cols-3 gap-4" data-testid="mc-foundations">
              <Foundation
                icon={<BookOpen size={18} />}
                title="ELLP"
                body="National framework for ELL assessment and teaching"
              />
              <Foundation
                icon={<Globe2 size={18} />}
                title="NZ Curriculum"
                body="Meaningful contribution across every learning area"
              />
              <Foundation
                icon={<Users2 size={18} />}
                title="Culturally Sustaining Pedagogy"
                body="Honouring the cultural wealth ELL students bring"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Detail({ icon, label, value }) {
  return (
    <li className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-md bg-[var(--sage)] text-[var(--forest)] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-[0.72rem] uppercase tracking-[0.2em] text-[var(--slate)] font-semibold">
          {label}
        </div>
        <div className="font-display font-semibold text-[var(--charcoal)] mt-0.5">{value}</div>
      </div>
    </li>
  );
}

function Strand({ icon, label, title, body, testid }) {
  return (
    <div className="card flex gap-5" data-testid={testid}>
      <div className="w-12 h-12 rounded-lg bg-[var(--forest)] text-white flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <div className="overline">{label}</div>
        <h4 className="font-display font-semibold text-lg md:text-xl text-[var(--charcoal)] mt-1">
          {title}
        </h4>
        <p className="mt-2 text-[var(--slate)] leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function Foundation({ icon, title, body }) {
  return (
    <div className="p-4 rounded-lg bg-white border border-[var(--border)]">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md bg-[var(--sage)] text-[var(--forest)] flex items-center justify-center">
          {icon}
        </div>
        <div className="font-display font-semibold text-sm">{title}</div>
      </div>
      <p className="mt-2 text-xs text-[var(--slate)] leading-snug">{body}</p>
    </div>
  );
}
